import {
  COW_PROTOCOL_SETTLEMENT_CONTRACT_ADDRESS,
  COW_PROTOCOL_VAULT_RELAYER_ADDRESS,
  OrderBookApi,
  type OrderCreation,
  type OrderParameters,
  type OrderQuoteResponse,
  OrderQuoteSideKindSell,
  PriceQuality,
  SigningScheme,
  SupportedChainId,
} from './cowswap/sdk';
import type { Address } from '$types/address';
import type { AssetTypeIdentifier } from '$types/assetTypeIdentifier';
import { ASSET_TYPES_ENTITIES } from '$lib/constants/assetTypes';
import { encodeFunctionData, type Hex, parseUnits, zeroAddress } from 'viem';
import { publicClient } from '$utils/publicClient';
import type { MetaTransaction } from '$types/transaction';
import { AbiCoWSettlement, AbiIERC20, AbiWXDAI } from '$lib/constants/abis';
import { TOKEN_ADDRESSES_ENTITIES } from '$lib/constants/tokenAddresses';
import { getWrappedERC20Address } from './circlesV2Utils/swapCrc';
import { DEFAULT_AFFILIATE_GROUP, getAGGroupAddress } from '$lib/constants/lbpTestGroup';
import { addAddressToComethSponsorList } from './circlesV2Utils/comethSponsor';
import { DemurrageOrInflationary } from '$types/DemurrageOrInflationary';

const orderBookApi = new OrderBookApi({ chainId: SupportedChainId.GNOSIS_CHAIN });

export const getOrder = async (orderUid: `0x${string}`) => {
  return await orderBookApi.getOrder(orderUid);
};

/**
 * This is temporary, until cowswap adds support for Monerium V2
 */
export const temporaryRemapMoneriumV1 = (address: Address) => {
  // eure

  if (address?.toLowerCase() === '0x420ca0f9b9b604ce0fd9c18ef134c705e5fa3430') {
    return '0xcB444e90D8198415266c6a2724b7900fb12FC56E';
  }
  // gbpe
  if (address?.toLowerCase() === '0x8e34bfec4f6eb781f9743d9b4af99cd23f9b7053') {
    return '0x5Cb9073902F2035222B9749F8fB0c9BFe5527108';
  }
  // everything else
  return address;
};

const cachedQuotes: Record<string, OrderQuoteResponse> = {};

export const getQuote = async (
  address: Address,
  amount: number,
  fromAssetTypeId: AssetTypeIdentifier,
  toAssetTypeId: AssetTypeIdentifier,
  options: {
    bypassCheck?: boolean;
    signal?: AbortSignal;
    receiver?: Address;
    isTopUpDeal?: boolean;
  } = { bypassCheck: false },
) => {
  if (!amount) {
    throw new Error('Amount must be defined and bigger than 0');
  }

  if (fromAssetTypeId === toAssetTypeId) {
    throw new Error('You can not swap to the same token');
  }

  const fromAssetType = ASSET_TYPES_ENTITIES[fromAssetTypeId];
  const toAssetType = ASSET_TYPES_ENTITIES[toAssetTypeId];

  if (!fromAssetType.swap && !options?.bypassCheck) {
    throw new Error('Unsupported from token');
  }
  if (!toAssetType.swap) {
    throw new Error('Unsupported to token');
  }

  // crc case
  let wrappedGcrcAddress: Address | undefined;
  if (fromAssetTypeId === 'crc' || toAssetTypeId === 'crc') {
    const primaryGroup = options?.isTopUpDeal ? DEFAULT_AFFILIATE_GROUP : getAGGroupAddress();
    wrappedGcrcAddress = await getWrappedERC20Address(
      primaryGroup,
      DemurrageOrInflationary.INFLATIONARY,
    );
    await addAddressToComethSponsorList(wrappedGcrcAddress);
    if (wrappedGcrcAddress === zeroAddress) {
      throw new Error(
        'Wrapped address is zero, have you called ensureERC20() on the ILiftERC20 contract?',
      );
    }
  }

  const sellToken =
    fromAssetType.id === 'crc' && wrappedGcrcAddress
      ? wrappedGcrcAddress
      : fromAssetType.contractAddress
        ? fromAssetType.contractAddress
        : fromAssetType.include
          ? ASSET_TYPES_ENTITIES[fromAssetType.include].contractAddress
          : undefined;

  if (!sellToken) {
    throw new Error('Undefined sell token');
  }

  const buyToken =
    toAssetType.id === 'crc' && wrappedGcrcAddress
      ? wrappedGcrcAddress
      : toAssetType.contractAddress
        ? toAssetType.contractAddress
        : toAssetType.include
          ? ASSET_TYPES_ENTITIES[toAssetType.include].contractAddress
          : undefined;

  if (!buyToken) {
    throw new Error('Undefined buy token');
  }

  const cacheKey = sellToken + buyToken + amount;

  if (cachedQuotes[cacheKey] && new Date(cachedQuotes[cacheKey].expiration) > new Date()) {
    return cachedQuotes[cacheKey];
  }

  const quote = await orderBookApi.getQuote(
    {
      priceQuality: PriceQuality.OPTIMAL,
      sellToken: temporaryRemapMoneriumV1(sellToken),
      buyToken: temporaryRemapMoneriumV1(buyToken),
      from: address,
      receiver: options.receiver ?? address,
      sellAmountBeforeFee: parseUnits(amount.toString(), fromAssetType.decimals).toString(),
      kind: OrderQuoteSideKindSell.SELL,
    },
    {
      signal: options.signal ?? undefined,
      env: 'prod',
    },
  );

  cachedQuotes[cacheKey] = quote;

  return quote;
};

/**
 * Verify if the user has enough allowance and wxDAI balance to perform the swap.
 * CoW Swap does not swap native tokens, so xDAI needs to be wraped to wxDAI.
 * It only wraps on sell operations. Does not unwrap on buy operations.
 * @returns Array of transactions to be signed
 */
const verifyAllowanceAndWxDAI = async (
  userAddress: `0x${string}`,
  sellToken: `0x${string}`,
  sellAmount: string,
): Promise<{ presignRawTxs: MetaTransaction[] }> => {
  const presignRawTxs: MetaTransaction[] = [];
  if (
    temporaryRemapMoneriumV1(sellToken).toLowerCase() ===
    TOKEN_ADDRESSES_ENTITIES.wxdai.toLowerCase()
  ) {
    // check wxdai balance, if below required, wrap the remaining amount

    const balance = (await publicClient.readContract({
      address: temporaryRemapMoneriumV1(sellToken),
      abi: AbiIERC20,
      functionName: 'balanceOf',
      args: [userAddress],
    })) as bigint;

    if (balance < BigInt(sellAmount)) {
      const presignedDepositCallData = encodeFunctionData({
        abi: AbiWXDAI,
        functionName: 'deposit',
      });

      const presignRawTxDeposit: MetaTransaction = {
        to: temporaryRemapMoneriumV1(sellToken),
        value: BigInt(sellAmount) - balance,
        data: presignedDepositCallData,
      };

      presignRawTxs.push(presignRawTxDeposit);
    }
  }

  // check erc20 allowance, if below required, set new approval

  const allowance = (await publicClient.readContract({
    address: temporaryRemapMoneriumV1(sellToken),
    abi: AbiIERC20,
    functionName: 'allowance',
    args: [userAddress, COW_PROTOCOL_VAULT_RELAYER_ADDRESS[SupportedChainId.GNOSIS_CHAIN]],
  })) as bigint;

  if (allowance < BigInt(sellAmount)) {
    const presignedApproveCallData = encodeFunctionData({
      abi: AbiIERC20,
      functionName: 'approve',
      args: [COW_PROTOCOL_VAULT_RELAYER_ADDRESS[SupportedChainId.GNOSIS_CHAIN], sellAmount],
    });
    const presignRawTxApproval: MetaTransaction = {
      to: temporaryRemapMoneriumV1(sellToken),
      value: 0n,
      data: presignedApproveCallData,
    };

    presignRawTxs.push(presignRawTxApproval);
  }
  return { presignRawTxs };
};

/**
 * Order a swap on CoW Swap.
 * It will verify the allowance and wxDAI balance before sending the order.
 * Includes fee on sell amount and accounts for 1% slippage.
 * @returns Array of transactions to be signed and the orderUid
 */
export const order = async (
  quote: OrderParameters,
  userAddress: `0x${string}`,
  quoteId?: number,
): Promise<{
  presignRawTxs: MetaTransaction[];
  orderUid: string;
}> => {
  const presignRawTxs: MetaTransaction[] = [];

  const sellAmount = (BigInt(quote.sellAmount) + BigInt(quote.feeAmount)).toString();
  const buyAmount = ((BigInt(quote.buyAmount) * 99n) / 100n).toString();

  const { presignRawTxs: presignRawTxsXdai } = await verifyAllowanceAndWxDAI(
    userAddress,
    temporaryRemapMoneriumV1(quote.sellToken as `0x${string}`),
    sellAmount,
  );

  if (presignRawTxsXdai.length > 0) {
    presignRawTxs.push(...presignRawTxsXdai);
  }

  // swap call

  const order: OrderCreation = {
    ...quote,
    sellAmount,
    buyAmount,
    feeAmount: '0',
    partiallyFillable: false,
    from: userAddress,
    signature: userAddress,
    signingScheme: SigningScheme.PRESIGN,
  };

  const orderUid = await orderBookApi.sendOrder({ ...order, quoteId });
  const presignCallData = encodeFunctionData({
    abi: AbiCoWSettlement,
    functionName: 'setPreSignature',
    args: [orderUid, true],
  });

  const presignRawTxSwap: MetaTransaction = {
    to: COW_PROTOCOL_SETTLEMENT_CONTRACT_ADDRESS[SupportedChainId.GNOSIS_CHAIN] as Hex,
    value: 0n,
    data: presignCallData,
  };

  presignRawTxs.push(presignRawTxSwap);

  return { presignRawTxs, orderUid };
};

export const getQuoteAmountsWithSlippage = (params: {
  quote: OrderParameters;

  slippagePercentBps: number;
}): OrderParameters => {
  const { quote, slippagePercentBps } = params;
  const ONE_HUNDRED_BPS = BigInt(100 * 100);

  const getSlippageAmount = (amount: string) =>
    (BigInt(amount) * BigInt(slippagePercentBps)) / ONE_HUNDRED_BPS;

  /**
   * Same rules apply for slippage as for partner fees
   */
  const afterSlippage = {
    sellAmount: quote.sellAmount,
    buyAmount: (BigInt(quote.buyAmount) - getSlippageAmount(quote.buyAmount)).toString(),
  };

  return {
    ...quote,
    ...afterSlippage,
  };
};
